import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { documentationApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import PageHeader from '../../../components/dashboard/PageHeader'
import { Field, Input, Select } from '../../../components/ui/Field'

const FONT_FAMILY_OPTIONS = [
  { label: 'Times New Roman', value: "'Times New Roman', serif" },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Calibri', value: 'Calibri, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' }
]

const PAGE_SIZE_OPTIONS = [
  { label: 'A4', width: 210, height: 297 },
  { label: 'Letter', width: 215.9, height: 279.4 }
]

function escapeHtml (value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function chapterToHtml (chapter) {
  const heading = `<h2>${escapeHtml(chapter?.title || 'Untitled Chapter')}</h2>`
  const bodyLines = String(chapter?.content || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

  const body = bodyLines.length
    ? bodyLines.map((line) => `<p>${escapeHtml(line)}</p>`).join('')
    : '<p></p>'

  return `${heading}${body}`
}

const WordEditor = () => {
  const [searchParams] = useSearchParams()
  const [content, setContent] = useState('<p>Start writing your document here...</p>')
  const [title, setTitle] = useState('my-document')
  const [fontFamily, setFontFamily] = useState(FONT_FAMILY_OPTIONS[0].value)
  const [fontSize, setFontSize] = useState(12)
  const [lineHeight, setLineHeight] = useState(1.5)
  const [pageSize, setPageSize] = useState('A4')
  const [marginTop, setMarginTop] = useState(25.4)
  const [marginRight, setMarginRight] = useState(25.4)
  const [marginBottom, setMarginBottom] = useState(25.4)
  const [marginLeft, setMarginLeft] = useState(25.4)
  const [loadingDoc, setLoadingDoc] = useState(false)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    const docId = searchParams.get('docId')
    if (!docId) return

    let mounted = true
    setLoadingDoc(true)
    setLoadError('')

    documentationApi.get(docId)
      .then((res) => {
        if (!mounted) return
        const doc = res?.data?.data || null
        const chapters = Array.isArray(doc?.chapters) ? doc.chapters : []
        const html = chapters.map((chapter) => chapterToHtml(chapter)).join('<hr />')
        if (html) setContent(html)
        const fromProject = doc?.project?.title || doc?.title || 'project-document'
        setTitle(String(fromProject).trim() || 'project-document')
      })
      .catch((e) => {
        if (!mounted) return
        setLoadError(e?.response?.data?.message || e.message || 'Could not load documentation for editing.')
      })
      .finally(() => {
        if (mounted) setLoadingDoc(false)
      })

    return () => {
      mounted = false
    }
  }, [searchParams])

  const modules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'clean']
      ]
    }),
    []
  )

  const formats = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'align',
    'blockquote',
    'code-block',
    'link'
  ]

  const page = PAGE_SIZE_OPTIONS.find((item) => item.label === pageSize) || PAGE_SIZE_OPTIONS[0]
  const pageWidthPx = Math.max(640, Math.round((page.width * 96) / 25.4))
  const pageMinHeightPx = Math.round((page.height * 96) / 25.4)

  const editorStyle = {
    fontFamily,
    fontSize: `${fontSize}px`,
    lineHeight: Number(lineHeight),
    paddingTop: `${marginTop}mm`,
    paddingRight: `${marginRight}mm`,
    paddingBottom: `${marginBottom}mm`,
    paddingLeft: `${marginLeft}mm`,
    minHeight: `${pageMinHeightPx}px`,
    backgroundColor: 'white'
  }

  const createDocumentHtml = () => `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title || 'document'}</title>
  <style>
    @page {
      size: ${pageSize};
      margin: ${marginTop}mm ${marginRight}mm ${marginBottom}mm ${marginLeft}mm;
    }
    body {
      font-family: ${fontFamily};
      font-size: ${fontSize}px;
      line-height: ${lineHeight};
      margin: 0;
      color: #111827;
    }
  </style>
</head>
<body>
${content}
</body>
</html>`

  const exportAsDoc = () => {
    const html = createDocumentHtml()
    const blob = new Blob([html], { type: 'application/msword;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${title || 'document'}.doc`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const printDocument = () => {
    const html = createDocumentHtml()
    const printWindow = window.open('', '_blank', 'width=1100,height=800')
    if (!printWindow) return
    printWindow.document.open()
    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
  }

  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow="Tools"
        title="Word editor"
        description="Format and export your work online."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={printDocument}>
              Print / PDF
            </Button>
            <Button size="sm" onClick={exportAsDoc}>
              Export .doc
            </Button>
          </div>
        }
      />
      {loadingDoc ? <p className="-mt-4 text-sm text-slate-500">Loading project document...</p> : null}
      {loadError ? <p className="-mt-4 text-sm text-red-600">{loadError}</p> : null}

      <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm md:p-6 space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Field label="Document title">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="h-9"
            />
          </Field>

          <Field label="Font family">
            <Select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value)}
              className="h-9"
            >
              {FONT_FAMILY_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>
                  {font.label}
                </option>
              ))}
            </Select>
          </Field>

          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Font size</span>
            <input
              type="number"
              min="8"
              max="72"
              className="input input-bordered input-sm"
              value={fontSize}
              onChange={(e) => setFontSize(Number(e.target.value) || 12)}
            />
          </label>

          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Line spacing</span>
            <input
              type="number"
              min="1"
              max="3"
              step="0.1"
              className="input input-bordered input-sm"
              value={lineHeight}
              onChange={(e) => setLineHeight(Number(e.target.value) || 1.5)}
            />
          </label>

          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Page size</span>
            <select
              className="select select-bordered select-sm"
              value={pageSize}
              onChange={(e) => setPageSize(e.target.value)}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size.label} value={size.label}>
                  {size.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Top margin (mm)</span>
            <input
              type="number"
              min="0"
              className="input input-bordered input-sm"
              value={marginTop}
              onChange={(e) => setMarginTop(Number(e.target.value) || 0)}
            />
          </label>
          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Right margin (mm)</span>
            <input
              type="number"
              min="0"
              className="input input-bordered input-sm"
              value={marginRight}
              onChange={(e) => setMarginRight(Number(e.target.value) || 0)}
            />
          </label>
          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Bottom margin (mm)</span>
            <input
              type="number"
              min="0"
              className="input input-bordered input-sm"
              value={marginBottom}
              onChange={(e) => setMarginBottom(Number(e.target.value) || 0)}
            />
          </label>
          <label className="form-control">
            <span className="label-text text-xs text-slate-600 mb-1">Left margin (mm)</span>
            <input
              type="number"
              min="0"
              className="input input-bordered input-sm"
              value={marginLeft}
              onChange={(e) => setMarginLeft(Number(e.target.value) || 0)}
            />
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-100 p-2 md:p-4 overflow-x-auto">
        <div
          className="mx-auto bg-white shadow-sm border border-slate-200"
          style={{ width: `${pageWidthPx}px`, maxWidth: '100%' }}
        >
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            style={editorStyle}
          />
        </div>
      </div>
    </section>
  )
}

export default WordEditor
